import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { sfx } from "@/lib/sfx";
import { useToast } from "@/contexts/ToastContext";

interface Token {
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
}

export function TokensTab() {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [freshToken, setFreshToken] = useState<{ name: string; raw: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();

  const refresh = useCallback(async () => {
    try { setTokens(await api<Token[]>("/api/account/tokens")); }
    catch (e) { setErr(e instanceof Error ? e.message : "Erreur"); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await api<{ id: string; name: string; token: string; created_at: string }>(
        `/api/account/tokens?name=${encodeURIComponent(name.trim() || "sans nom")}`,
        { method: "POST", body: "{}" },
      );
      sfx.chirp();
      setFreshToken({ name: r.name, raw: r.token });
      setName("");
      await refresh();
    } catch (e) { setErr(e instanceof Error ? e.message : "Erreur"); }
    finally { setBusy(false); }
  };

  const revoke = async (id: string) => {
    sfx.tap();
    if (!confirm("Révoquer ce token ? Il sera immédiatement invalide partout où il est utilisé.")) return;
    try { await api(`/api/account/tokens/${id}`, { method: "DELETE" }); await refresh(); }
    catch (e) { setErr(e instanceof Error ? e.message : "Erreur"); }
  };

  const copyRaw = async () => {
    if (!freshToken) return;
    try {
      await navigator.clipboard.writeText(freshToken.raw);
      sfx.pop();
      toast.show("Token copié dans le presse-papier ✓");
    } catch { toast.show("Copie impossible — sélectionne et Ctrl+C"); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="muted" style={{ fontSize: 13 }}>
        Tokens d'accès personnels — jamais expirés, utilisables comme <code>Authorization: Bearer slt_…</code>
        pour tes scripts (bridge Discord, home assistant, cron, etc.).
      </div>

      {/* Freshly created token — ONE-TIME reveal */}
      {freshToken && (
        <div style={{
          padding: 14, borderRadius: 10, border: "1px solid var(--accent)",
          background: "rgba(167,139,255,.10)",
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            ✓ Token « {freshToken.name} » créé
          </div>
          <div style={{ fontSize: 12, color: "#fbbf24", marginBottom: 10 }}>
            ⚠ Copie-le maintenant — il ne sera plus jamais affiché.
          </div>
          <code style={{
            display: "block", padding: "10px 12px", background: "var(--bg-elev-2)",
            borderRadius: 8, fontFamily: "ui-monospace, monospace",
            fontSize: 12, wordBreak: "break-all", userSelect: "all",
          }}>{freshToken.raw}</code>
          <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
            <button className="btn" onClick={() => setFreshToken(null)}>Fermer</button>
            <button className="btn primary" onClick={() => void copyRaw()}>Copier</button>
          </div>
        </div>
      )}

      <form onSubmit={create} style={{ display: "flex", gap: 8 }}>
        <input value={name} onChange={(e) => setName(e.target.value)}
               placeholder="Nom du token (ex : Discord bridge, Home Assistant, …)"
               maxLength={120}
               style={{ flex: 1, padding: "10px 12px", background: "var(--bg-elev-2)",
                        border: "1px solid var(--line)", borderRadius: 8, color: "var(--fg)" }} />
        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? "…" : "+ Nouveau"}
        </button>
      </form>

      <div style={{ maxHeight: "40vh", overflowY: "auto", display: "flex",
                    flexDirection: "column", gap: 6 }}>
        {tokens.length === 0 && (
          <div className="muted" style={{ padding: 12, fontSize: 13 }}>
            Aucun token actif. Crée-en un via le champ ci-dessus.
          </div>
        )}
        {tokens.map((t) => (
          <div key={t.id} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
            background: "var(--bg-elev-2)", borderRadius: 8,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }} className="trunc">{t.name}</div>
              <div className="muted" style={{ fontSize: 12 }}>
                Créé le {new Date(t.created_at).toLocaleDateString("fr-FR")}
                {t.last_used_at && ` · dernière utilisation ${new Date(t.last_used_at).toLocaleString("fr-FR")}`}
                {!t.last_used_at && " · jamais utilisé"}
              </div>
            </div>
            <button className="btn" onClick={() => void revoke(t.id)}
                    style={{ padding: "4px 10px", fontSize: 12,
                             color: "#f87171", borderColor: "#7a2f2f" }}>Révoquer</button>
          </div>
        ))}
      </div>

      {err && <div style={{ color: "#f87171", fontSize: 13 }}>{err}</div>}
    </div>
  );
}
